import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const inputClassName = "w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a4731]/25 focus:border-[#1a4731]/40 transition-all";
  const labelClassName = "block text-sm font-medium text-slate-600 mb-1.5";
  const primaryClassName = "block w-full py-2.5 text-center bg-[#1a4731] hover:bg-[#153a27] text-white font-semibold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/password-reset/`, { email });
    } catch {
      // Always show success to prevent email enumeration
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#f8f7f4]">
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden bg-[#1a4731]">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-16 w-44 h-44 rounded-full bg-white/[0.03]" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">Carlo</span>
        </div>

        <div className="relative z-10">
          <h1 className="text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
            Smart<br />Restaurant<br />Operations
          </h1>
          <p className="text-white/50 text-lg leading-relaxed max-w-xs">
            Manage your sales, inventory, staff and analytics — all in one place.
          </p>
        </div>

        <p className="text-white/25 text-sm relative z-10">© {new Date().getFullYear()} Carlo</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-[380px]">
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div className="w-11 h-11 bg-[#1a4731] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 tracking-tight">Carlo</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-1.5">{t('passwordReset.forgotTitle')}</h2>
          <p className="text-slate-400 text-sm mb-8">{t('passwordReset.forgotSubtitle')}</p>

          {sent ? (
            <div className="space-y-4">
              <div className="bg-green-50 text-green-700 text-sm px-3 py-3 rounded-lg border border-green-200">
                <p className="font-medium mb-1">{t('passwordReset.successTitle')}</p>
                <p>{t('passwordReset.successMessage')}</p>
              </div>
              <Link
                to="/login"
                className={primaryClassName}
              >
                {t('passwordReset.backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className={labelClassName}>
                  {t('passwordReset.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClassName}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={primaryClassName}
              >
                {loading ? t('passwordReset.sending') : t('passwordReset.sendLink')}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-slate-400">
            <Link to="/login" className="text-[#1a4731] hover:text-[#153a27] font-medium transition-colors">
              {t('passwordReset.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
